import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, matchPath, useLocation, useParams } from 'react-router-dom';
import { SupportErrorMessage } from '../components/overlays/EngineeringSupportBanner';

export type SemanticComponentConfig = {
  id?: string;
  status?: string;
  semanticPath?: string;
  integration?: {
    path?: string;
    query?: Record<string, string>;
  };
};

export type SemanticComponentsConfig = {
  components?: SemanticComponentConfig[];
};

export type SemanticRouteAction = {
  id?: string;
  action?: string;
  name?: string;
  role?: string;
  selector?: string;
  selectorIndex?: number;
  expectText?: string;
  waitForText?: string;
};

export type SemanticRouteEntry = {
  id?: string;
  semanticPath?: string;
  ui?: {
    actionIds?: string[];
  };
};

export type SemanticRoutesConfig = {
  module?: string;
  routeRoot?: string;
  semanticRoot?: string;
  routes?: SemanticRouteEntry[];
  actions?: SemanticRouteAction[];
};

export type SemanticInternalRenderInput = {
  component: SemanticComponentConfig;
  componentId: string;
  internalPath: string;
  params: Record<string, string | undefined>;
  semanticPath: string;
  pathname: string;
};

type InternalRoutesConfig = {
  resolvePath?: (componentId: string, params: Record<string, string | undefined>) => string | null;
  routes?: ReactNode;
  render?: (input: SemanticInternalRenderInput) => ReactNode;
};

type SemanticRouteErrorCode =
  | 'missing-component'
  | 'inactive-component'
  | 'missing-internal-binding'
  | 'bridged-target-blocked';

type SemanticRouteError = {
  code: SemanticRouteErrorCode;
  message: string;
};

export type SemanticRouteAdapterProps = {
  moduleKey: string;
  componentsConfig: SemanticComponentsConfig;
  routesConfig: SemanticRoutesConfig;
  internal?: InternalRoutesConfig;
  mapIntegrationPath?: (path: string | null) => string | null;
  strictNoFallback?: boolean;
  blockBridgedTarget?: boolean;
};

const ACTION_PLAN_KEY = '__triedgeActionPlan';
const ROUTE_LOADING_SELECTORS = [
  '[data-testid*="loading" i]',
  '[data-test-id*="loading" i]',
  '[data-testid*="skeleton" i]',
  '[data-test-id*="skeleton" i]',
  '[aria-label*="loading" i]',
  '[aria-busy="true"]',
  '[data-loading="true"]',
];
const ROUTE_LOADING_TEXT_PATTERN = /\b(loading|fetching|initializing)\b/i;

const isLocalDevHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');
};
const resolveActionPlan = (
  pathname: string,
  routeConfig: SemanticRoutesConfig
) => {
  const entries = routeConfig.routes || [];
  const match = entries.find((entry) =>
    entry.semanticPath ? matchPath({ path: entry.semanticPath, end: true }, pathname) : false
  );
  if (!match?.id) return null;
  const actionIds = match.ui?.actionIds ?? [];
  if (!actionIds.length) return null;
  const actionMap = new Map<string, SemanticRouteAction>();
  (routeConfig.actions || []).forEach((action) => {
    if (action?.id) {
      actionMap.set(action.id, action);
    }
  });
  const actions = actionIds
    .map((id) => actionMap.get(id))
    .filter(Boolean) as SemanticRouteAction[];
  if (!actions.length) return null;
  return {
    id: `${routeConfig.module || 'module'}:${match.id}`,
    module: routeConfig.module || 'module',
    routeId: match.id,
    semanticPath: pathname,
    actions: actions.map((action) => ({
      id: action.id,
      action: action.action,
      name: action.name,
      role: action.role,
      selector: action.selector,
      selectorIndex: action.selectorIndex,
      expectText: action.expectText,
      waitForText: action.waitForText,
    })),
  };
};

const persistActionPlan = (plan: ReturnType<typeof resolveActionPlan>) => {
  try {
    if (!plan) {
      window.sessionStorage.removeItem(ACTION_PLAN_KEY);
      return;
    }
    window.sessionStorage.setItem(ACTION_PLAN_KEY, JSON.stringify(plan));
  } catch {
    // ignore storage errors
  }
};

const isElementVisible = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const hasVisibleLoadingMarker = (root: HTMLElement): boolean => {
  for (const selector of ROUTE_LOADING_SELECTORS) {
    const matches = root.querySelectorAll(selector);
    for (const node of matches) {
      if (isElementVisible(node)) {
        return true;
      }
    }
  }

  const textCandidates = root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,div,li,strong,small');
  const maxChecks = Math.min(textCandidates.length, 300);
  for (let index = 0; index < maxChecks; index += 1) {
    const node = textCandidates[index];
    if (!isElementVisible(node)) {
      continue;
    }
    const text = (node.textContent || '').trim();
    if (text && ROUTE_LOADING_TEXT_PATTERN.test(text)) {
      return true;
    }
  }
  return false;
};

export function SemanticRouteAdapter({
  moduleKey,
  componentsConfig,
  routesConfig,
  internal,
  mapIntegrationPath,
  strictNoFallback = true,
  blockBridgedTarget = true,
}: SemanticRouteAdapterProps) {
  const location = useLocation();
  const params = useParams();
  const pathname = location.pathname;
  const routeRootRef = useRef<HTMLElement | null>(null);
  const [routeReady, setRouteReady] = useState(false);
  const resolvedSemanticPath = useMemo(() => {
    const mapped = mapIntegrationPath?.(pathname) ?? pathname;
    return mapped || pathname;
  }, [mapIntegrationPath, pathname]);

  const component = useMemo(() => {
    const entries = componentsConfig.components || [];
    return entries.find((entry) =>
      entry.semanticPath ? matchPath({ path: entry.semanticPath, end: true }, resolvedSemanticPath) : false
    );
  }, [componentsConfig.components, resolvedSemanticPath]);

  const internalPath = useMemo(() => {
    const componentId = component?.id;
    if (!componentId) return null;
    if (!internal?.resolvePath) return null;
    return internal.resolvePath(componentId, params);
  }, [component?.id, internal, params]);

  const hasInternalRenderer = Boolean(internal?.render);
  const hasInternalRoutes = Boolean(internal?.routes);
  const internalRoutingEnabled = Boolean(internal?.resolvePath && (hasInternalRoutes || hasInternalRenderer));
  const internalReady = Boolean(internalPath && (hasInternalRoutes || hasInternalRenderer));

  const actionPlan = useMemo(
    () => resolveActionPlan(resolvedSemanticPath, routesConfig),
    [resolvedSemanticPath, routesConfig]
  );

  const routeError = useMemo<SemanticRouteError | null>(() => {
    const pathCandidates = [pathname, resolvedSemanticPath, internalPath].filter(Boolean) as string[];
    const isBridgedTarget = pathCandidates.some((candidate) => candidate.includes('/bridged/'));
    if (blockBridgedTarget && isBridgedTarget) {
      return {
        code: 'bridged-target-blocked',
        message: `[SemanticRouteAdapter] Bridged route targets are blocked for module "${moduleKey}" at "${pathname}".`,
      };
    }
    if (!component) {
      return {
        code: 'missing-component',
        message: strictNoFallback
          ? `[SemanticRouteAdapter] Missing semantic component mapping for module "${moduleKey}" at "${pathname}". Strict no-fallback mode is enabled.`
          : `[SemanticRouteAdapter] Missing semantic component mapping for module "${moduleKey}" at "${pathname}". Exact route mapping is required.`,
      };
    }
    if (component.status && ['inactive', 'disabled', 'deprecated'].includes(component.status)) {
      return {
        code: 'inactive-component',
        message: `[SemanticRouteAdapter] Inactive semantic component "${component.id ?? 'unknown'}" for module "${moduleKey}" at "${pathname}".`,
      };
    }
    if (!internalRoutingEnabled) {
      return {
        code: 'missing-internal-binding',
        message: `[SemanticRouteAdapter] Internal route binding is required for module "${moduleKey}" at "${pathname}".`,
      };
    }
    if (internalRoutingEnabled && (!internalReady || !internalPath)) {
      return {
        code: 'missing-internal-binding',
        message: `[SemanticRouteAdapter] Missing internal route binding for semantic component "${component.id ?? 'unknown'}" in module "${moduleKey}" at "${pathname}".`,
      };
    }
    return null;
  }, [
    blockBridgedTarget,
    component,
    internalPath,
    internalReady,
    internalRoutingEnabled,
    moduleKey,
    pathname,
    resolvedSemanticPath,
    strictNoFallback,
  ]);

  const activeComponent = component ?? null;

  useEffect(() => {
    if (routeError) {
      persistActionPlan(null);
      return;
    }
    persistActionPlan(actionPlan);
  }, [actionPlan, routeError]);

  useEffect(() => {
    if (!routeError) return;
    console.error(routeError.message, {
      code: routeError.code,
      moduleKey,
      pathname,
      semanticPath: resolvedSemanticPath,
    });
  }, [moduleKey, pathname, resolvedSemanticPath, routeError]);

  useEffect(() => {
    if (routeError || !activeComponent) return;
    console.info('[SemanticRouteAdapter] Resolved semantic route', {
      moduleKey,
      pathname,
      semanticPath: resolvedSemanticPath,
      componentId: activeComponent.id,
      status: activeComponent.status,
      internalRoutingEnabled,
      internalPath,
    });
  }, [
    activeComponent?.id,
    activeComponent?.status,
    internalPath,
    internalRoutingEnabled,
    moduleKey,
    pathname,
    routeError,
    resolvedSemanticPath,
  ]);

  useEffect(() => {
    if (routeError || !activeComponent || !internalRoutingEnabled) {
      setRouteReady(false);
      return;
    }
    if (typeof window === 'undefined') {
      setRouteReady(true);
      return;
    }

    const evaluate = () => {
      const root = routeRootRef.current;
      if (!root) {
        setRouteReady(false);
        return;
      }
      setRouteReady(!hasVisibleLoadingMarker(root));
    };

    evaluate();
    const root = routeRootRef.current;
    if (!root) {
      return;
    }

    let timeoutId: number | null = null;
    const observer = new MutationObserver(() => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        evaluate();
      }, 48);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden', 'aria-busy', 'data-loading', 'data-testid', 'data-test-id'],
    });

    const intervalId = window.setInterval(evaluate, 320);

    return () => {
      observer.disconnect();
      window.clearInterval(intervalId);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    activeComponent?.id,
    internalPath,
    internalRoutingEnabled,
    pathname,
    routeError,
  ]);

  if (routeError) {
    return (
      <section
        data-testid={`${moduleKey}-semantic-route-error`}
        data-extracted-from={`owned:ystnj/semantic/${moduleKey}-route-error`}
        style={{ padding: 16 }}
      >
        <SupportErrorMessage>
          This route does not have a valid semantic mapping for your current shell path.
        </SupportErrorMessage>
        {isLocalDevHost() ? (
          <pre
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              overflowX: 'auto',
              background: '#0f172a',
              color: '#e2e8f0',
              fontSize: 12,
            }}
          >
            {routeError.message}
          </pre>
        ) : null}
      </section>
    );
  }

  if (!activeComponent) {
    return null;
  }

  if (!internalRoutingEnabled) {
    return null;
  }

  const routeRootTestId = `${moduleKey}-semantic-route-root`;
  const internalRouteKey = internalPath ?? activeComponent.id ?? pathname;
  const routesLocation =
    internalPath && internalPath !== pathname ? internalPath : undefined;

  if (internal?.render && activeComponent.id && internalPath) {
    return (
      <section
        key={internalRouteKey}
        ref={routeRootRef}
        data-testid={routeRootTestId}
        data-semantic-module={moduleKey}
        data-semantic-component-id={activeComponent.id}
        data-semantic-internal-path={internalPath}
        data-route-ready={routeReady ? 'true' : 'false'}
      >
        {internal.render({
          component: activeComponent,
          componentId: activeComponent.id,
          internalPath,
          params,
          semanticPath: resolvedSemanticPath,
          pathname,
        })}
      </section>
    );
  }

  return (
    <section
      key={internalRouteKey}
      ref={routeRootRef}
      data-testid={routeRootTestId}
      data-semantic-module={moduleKey}
      data-semantic-component-id={activeComponent.id}
      data-semantic-internal-path={internalPath ?? undefined}
      data-route-ready={routeReady ? 'true' : 'false'}
    >
      <Routes key={internalRouteKey} location={routesLocation}>
        {internal?.routes}
      </Routes>
    </section>
  );
}
